import React, { useEffect, useRef, useState } from 'react';
import { Search, UserCheck } from 'lucide-react';
import { apiMessage, searchCustomers } from '../services/serviceInboxApi';
import { JsonButton } from './JsonModal';

/**
 * CustomerSearch — look a ServiceTitan customer up by hand, across every tenant.
 *
 * For a message the automatic lookup could not settle: nobody found (a
 * different name, a spouse, a landlord), or more than one. Backed by the
 * platform's /api/customer-search: every word of the query somewhere in the
 * name and street, an email exactly, 4+ digits against phone numbers, and an
 * all-digit query as a customer id too.
 *
 * `onInspect`, when given, puts a `{ }` on each result that opens the customer
 * as ServiceTitan holds it. `onMark`, when given, puts a "Mark as match"
 * button on each result that is not RNC. There are no one-click searches from
 * the extracted signals (removed 2026-09-26): the lookup has already tried
 * those keys. `renderList(list, q)` replaces the result list entirely (the task
 * form draws its results as radio options); `q` is the query that produced
 * them, for highlighting.
 *
 * Clicking anywhere outside it clears the results. Typing searches on its own
 * once the person pauses for TYPE_AHEAD_MS; Enter or the button search at once.
 */

const TYPE_AHEAD_MS = 500;

// One entry per customer, addresses deduped: the platform returns a row per
// customer and service location, the same collapse the mailbox API does.
export const byCustomer = (rows) => {
  const seen = new Map();
  for (const r of rows || []) {
    const key = `${r.tenant_id}|${r.customer_id}`;
    let entry = seen.get(key);
    if (!entry) {
      entry = {
        key,
        tenant_id: r.tenant_id,
        customer_id: r.customer_id,
        name: r.name,
        office: r.tenant_location,
        customer_type: r.customer_type,
        is_rnc: r.is_rnc,
        active: r.active,
        addresses: [],
        // The phone or email that found it, when a contact is what matched.
        matched_contact_type: r.matched_contact_type || null,
        matched_contact: r.matched_contact || null,
      };
      seen.set(key, entry);
    }
    if (!entry.matched_contact && r.matched_contact) {
      entry.matched_contact_type = r.matched_contact_type;
      entry.matched_contact = r.matched_contact;
    }
    const loc = r.location || {};
    const line = [loc.street, loc.city, loc.state, loc.zip_code].filter(Boolean).join(', ');
    if (line && !entry.addresses.includes(line)) entry.addresses.push(line);
  }
  return [...seen.values()];
};

// What a result matched, mirroring the platform's rules (customer_search.py):
// every word of the query in the name or street, an email exactly (@), a
// phone number's digits (digits and phone punctuation, 4+ digits), or an
// all-digit query as the customer id. The results carry no email or phone, so
// those two can only be named, not highlighted.
const PHONE_SHAPE = /^[\d\s().+-]+$/;

const escapeRe = (t) => t.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

export const queryTokens = (q) => {
  const text = (q || '').trim();
  return text && !text.includes('@') ? text.toLowerCase().split(/\s+/) : [];
};

// The text with every query word marked, case-insensitively.
export const Highlight = ({ text, q }) => {
  const tokens = queryTokens(q);
  if (!text || !tokens.length) return text || null;
  const re = new RegExp(
    `(${[...tokens].sort((a, b) => b.length - a.length).map(escapeRe).join('|')})`,
    'gi'
  );
  return text.split(re).map((part, i) =>
    i % 2 ? (
      <mark key={i} className="rounded-sm bg-yellow-200 px-0.5 text-inherit">
        {part}
      </mark>
    ) : (
      part
    )
  );
};

// Whether the customer id itself is what matched.
export const idMatches = (c, q) => {
  const text = (q || '').trim();
  return /^\d+$/.test(text) && Number(text) === c.customer_id;
};

// A tag for a match that cannot be drawn on the result, or null. A result
// carrying the contact that matched draws that instead (ContactMatch).
export const matchNote = (c, q) => {
  const text = (q || '').trim();
  if (!text || c.matched_contact) return null;
  if (text.includes('@')) return 'email match';
  const hay = [c.name, ...(c.addresses || [])].join(' ').toLowerCase();
  if (queryTokens(text).every((t) => hay.includes(t))) return null;
  if (idMatches(c, text)) return null;
  if (text.replace(/\D/g, '').length >= 4 && PHONE_SHAPE.test(text)) return 'phone match';
  return null;
};

// A stored E.164 number (+17724614610) in the local form people read.
const formatPhone = (e164) => {
  const d = e164.replace(/\D/g, '');
  const n = d.length === 11 && d[0] === '1' ? d.slice(1) : d;
  return n.length === 10 ? `(${n.slice(0, 3)}) ${n.slice(3, 6)}-${n.slice(6)}` : e164;
};

// The formatted number with the searched digits marked, wherever the
// punctuation falls between them.
const PhoneDigits = ({ value, q }) => {
  const shown = formatPhone(value);
  const want = (q || '').replace(/\D/g, '');
  const digits = shown.replace(/\D/g, '');
  const at = want ? digits.indexOf(want) : -1;
  if (at < 0) return shown;
  let seen = 0;
  const parts = [];
  for (const ch of shown) {
    const isDigit = /\d/.test(ch);
    const hit = isDigit && seen >= at && seen < at + want.length;
    if (isDigit) seen += 1;
    const last = parts[parts.length - 1];
    if (last && last.hit === hit) last.text += ch;
    else parts.push({ hit, text: ch });
  }
  return parts.map((p, i) =>
    p.hit ? (
      <mark key={i} className="rounded-sm bg-yellow-200 px-0.5 text-inherit">
        {p.text}
      </mark>
    ) : (
      p.text
    )
  );
};

// The phone numbers or email that found this customer. Several matching numbers
// arrive joined with ", ".
export const ContactMatch = ({ c, q }) => {
  if (!c.matched_contact) return null;
  const isPhone = c.matched_contact_type === 'phone';
  const values = isPhone ? c.matched_contact.split(', ') : [c.matched_contact];
  return (
    <span className="block text-gray-600">
      <span className="text-gray-500">{isPhone ? 'Phone ' : 'Email '}</span>
      {values.map((v, i) => (
        <React.Fragment key={v}>
          {i > 0 && ', '}
          {isPhone ? (
            <PhoneDigits value={v} q={q} />
          ) : (
            <mark className="rounded-sm bg-yellow-200 px-0.5 text-inherit">{v}</mark>
          )}
        </React.Fragment>
      ))}
    </span>
  );
};

export const MatchTag = ({ note }) =>
  note ? (
    <span className="rounded bg-yellow-100 px-1.5 text-xs font-medium text-yellow-900 ring-1 ring-inset ring-yellow-300">
      {note}
    </span>
  ) : null;

export const MarkButton = ({ onClick, busy }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={busy}
    className="inline-flex shrink-0 items-center rounded-md border border-gray-300 bg-white px-2 py-1 text-xs text-gray-700 hover:bg-gray-50 disabled:opacity-50"
  >
    <UserCheck className="mr-1 h-3.5 w-3.5" />
    Mark as match
  </button>
);

const CustomerSearch = ({ initialQuery = '', onMark, onInspect, renderList }) => {
  const [query, setQuery] = useState(initialQuery);
  const [searching, setSearching] = useState(false);
  const [results, setResults] = useState(null);
  const [error, setError] = useState(null);
  // Results put away by a click elsewhere; the box keeps its text, and
  // focusing it again brings them back.
  const [hidden, setHidden] = useState(false);

  const rootRef = useRef(null);
  // Only typing starts a type-ahead search: not the prefill, not a reset.
  const typed = useRef(false);
  // The newest search; an older one answering late is dropped.
  const latest = useRef(0);

  const reset = () => {
    typed.current = false;
    setQuery(initialQuery);
    setResults(null);
    setError(null);
    setHidden(false);
  };

  // A different message: start over from its own signals.
  useEffect(reset, [initialQuery]); // eslint-disable-line

  // Clicking anywhere else puts the results away, like closing a dropdown,
  // and leaves what was typed in the box (2026-09-30).
  useEffect(() => {
    if ((!results && !error) || hidden) return undefined;
    const onDown = (e) => {
      // A JSON view opened from a result is not "away": closing it should
      // come back to the same results.
      if (e.target.closest?.('[data-json-modal]')) return;
      if (rootRef.current && !rootRef.current.contains(e.target)) {
        setHidden(true);
        setError(null);
      }
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [results, error, hidden]);

  const run = async (q) => {
    const text = (q ?? query).trim();
    if (text.length < 2) return;
    typed.current = false;
    const token = ++latest.current;
    if (q != null) setQuery(text);
    setHidden(false);
    setSearching(true);
    setError(null);
    try {
      const data = await searchCustomers(text);
      if (token !== latest.current) return;
      setResults({ q: text, list: byCustomer(data.candidates), truncated: data.truncated });
    } catch (err) {
      if (token !== latest.current) return;
      setResults(null);
      setError(apiMessage(err, 'Customer search failed.'));
    } finally {
      if (token === latest.current) setSearching(false);
    }
  };

  // Type-ahead: search once typing has paused.
  useEffect(() => {
    if (!typed.current || query.trim().length < 2) return undefined;
    // Skipped if Enter already searched in the meantime.
    const t = setTimeout(() => typed.current && run(), TYPE_AHEAD_MS);
    return () => clearTimeout(t);
  }, [query]); // eslint-disable-line

  return (
    <div ref={rootRef}>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          run();
        }}
        className="flex gap-2"
      >
        <input
          className="min-w-0 flex-1 rounded border border-gray-300 px-2 py-1 text-sm"
          placeholder="Name and street, phone, email or ST #"
          value={query}
          onFocus={() => setHidden(false)}
          onChange={(e) => {
            typed.current = true;
            setQuery(e.target.value);
          }}
        />
        <button
          type="submit"
          disabled={searching || query.trim().length < 2}
          className="inline-flex shrink-0 items-center rounded-md border border-gray-300 bg-white px-2.5 py-1 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
        >
          <Search className="mr-1 h-4 w-4" />
          {searching ? 'Searching…' : 'Search'}
        </button>
      </form>

      {error && <div className="mt-1 text-sm text-red-700">{error}</div>}
      {results && !hidden && (
        <div className="mt-2">
          {results.list.length === 0 ? (
            <div className="text-sm text-gray-500">No customers found for “{results.q}”.</div>
          ) : renderList ? (
            renderList(results.list, results.q)
          ) : (
            <ul className="max-h-80 divide-y divide-gray-200 overflow-auto rounded border border-gray-200 bg-white">
              {results.list.map((c) => (
                <li key={c.key} className="flex items-start gap-2 px-3 py-2 text-sm">
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-x-2">
                      <span className="font-semibold text-gray-900">
                        {c.name ? <Highlight text={c.name} q={results.q} /> : '(no name)'}
                      </span>
                      <span className="tabular-nums text-gray-500">
                        ST #{' '}
                        {idMatches(c, results.q) ? (
                          <mark className="rounded-sm bg-yellow-200 px-0.5 text-inherit">
                            {c.customer_id}
                          </mark>
                        ) : (
                          c.customer_id
                        )}
                      </span>
                      {onInspect && (
                        <JsonButton
                          label="View the ServiceTitan record"
                          onClick={() => onInspect(c)}
                        />
                      )}
                      <MatchTag note={matchNote(c, results.q)} />
                      {c.active === false && <span className="text-xs text-gray-500">inactive</span>}
                      {c.is_rnc && (
                        <span className="rounded bg-amber-50 px-1.5 text-xs font-medium text-amber-800 ring-1 ring-inset ring-amber-200">
                          RNC · out of scope
                        </span>
                      )}
                    </div>
                    <div className="text-gray-600">
                      {[c.office, c.customer_type].filter(Boolean).join(' · ')}
                    </div>
                    {c.addresses.map((a) => (
                      <div key={a} className="text-gray-600">
                        <Highlight text={a} q={results.q} />
                      </div>
                    ))}
                    <ContactMatch c={c} q={results.q} />
                  </div>
                  {onMark && !c.is_rnc && <MarkButton onClick={() => onMark(c)} />}
                </li>
              ))}
            </ul>
          )}
          {results.truncated && (
            <div className="mt-1 text-xs text-gray-500">More matched than shown; narrow the search.</div>
          )}
        </div>
      )}
    </div>
  );
};

export default CustomerSearch;
